import React, { useEffect, useRef } from 'react';

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const Legend = () => <div className="tier-legend" aria-label="Görsel açıklamaları"><span><i className="legend-swatch main" />Ana sayaç tüketimi</span><span><i className="legend-swatch sub" />Süzme sayaç tüketimi</span><span><i className="legend-swatch low" />Düşük fiyatlı bölüm</span><span><i className="legend-swatch high" />Yüksek fiyatlı bölüm</span></div>;

const TierAllocationVisual = ({ mode }) => {
  const stageRef = useRef(null);
  const mainFillRef = useRef(null);
  const subFillRef = useRef(null);
  const resultRef = useRef(null);
  const segmentRefs = useRef([]);
  const priority = mode === 'priority';

  useEffect(() => {
    const stage = stageRef.current;
    const mainFill = mainFillRef.current;
    const subFill = subFillRef.current;
    const result = resultRef.current;
    if (!stage || !mainFill || !subFill || !result) return undefined;

    let cancelled = false;
    const animations = new Set();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const steps = priority ? [
      { source: mainFill, sourceTotal: 150, amount: 150, remaining: 0, targetIndex: 0, targetStart: 0, targetAmount: 150, sourceClass: 'main', targetClass: 'low' },
      { source: subFill, sourceTotal: 150, amount: 90, remaining: 60, targetIndex: 1, targetStart: 150, targetAmount: 90, sourceClass: 'sub', targetClass: 'low' },
      { source: subFill, sourceTotal: 150, amount: 60, remaining: 0, targetIndex: 2, targetStart: 240, targetAmount: 60, sourceClass: 'sub', targetClass: 'high' }
    ] : [
      { source: mainFill, sourceTotal: 160, amount: 120, remaining: 40, targetIndex: 0, targetStart: 0, targetAmount: 120, sourceClass: 'main', targetClass: 'low' },
      { source: subFill, sourceTotal: 140, amount: 120, remaining: 20, targetIndex: 1, targetStart: 120, targetAmount: 120, sourceClass: 'sub', targetClass: 'low' },
      { source: mainFill, sourceTotal: 160, amount: 40, remaining: 0, targetIndex: 2, targetStart: 240, targetAmount: 40, sourceClass: 'main', targetClass: 'high' },
      { source: subFill, sourceTotal: 140, amount: 20, remaining: 0, targetIndex: 3, targetStart: 280, targetAmount: 20, sourceClass: 'sub', targetClass: 'high' }
    ];

    const reset = () => {
      mainFill.style.width = '100%';
      subFill.style.width = '100%';
      mainFill.classList.remove('is-empty');
      subFill.classList.remove('is-empty');
      mainFill.querySelector('span').textContent = `${priority ? 150 : 160} kWh`;
      subFill.querySelector('span').textContent = `${priority ? 150 : 140} kWh`;
      segmentRefs.current.forEach(segment => { if (segment) segment.style.width = '0'; });
      stage.querySelectorAll('.flying-consumption').forEach(node => node.remove());
    };

    const showFinal = () => {
      mainFill.style.width = '0';
      subFill.style.width = '0';
      mainFill.classList.add('is-empty');
      subFill.classList.add('is-empty');
      steps.forEach(step => { const segment = segmentRefs.current[step.targetIndex]; if (segment) segment.style.width = `${step.targetAmount / 3}%`; });
    };

    const transfer = async (step) => {
      if (cancelled) return;
      const stageRect = stage.getBoundingClientRect();
      const sourceRect = step.source.getBoundingClientRect();
      const resultRect = result.getBoundingClientRect();
      const flyingWidth = sourceRect.width * (step.amount / Math.max(step.amount + step.remaining, 1));
      const targetWidth = resultRect.width * (step.targetAmount / 300);
      const startLeft = sourceRect.right - flyingWidth - stageRect.left;
      const startTop = sourceRect.top - stageRect.top;
      const targetLeft = resultRect.left - stageRect.left + resultRect.width * (step.targetStart / 300);
      const targetTop = resultRect.top - stageRect.top;

      const flying = document.createElement('div');
      flying.className = `flying-consumption ${step.sourceClass}`;
      flying.textContent = `${step.amount} kWh`;
      Object.assign(flying.style, { left: `${startLeft}px`, top: `${startTop}px`, width: `${flyingWidth}px`, height: `${sourceRect.height}px` });
      stage.appendChild(flying);

      step.source.style.width = `${step.remaining / step.sourceTotal * 100}%`;
      step.source.querySelector('span').textContent = step.remaining ? `${step.remaining} kWh` : '';
      step.source.classList.toggle('is-empty', step.remaining === 0);
      const animation = flying.animate([
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: 'translate(0,-8px) scale(1.03)', opacity: 1, offset: .18 },
        { transform: `translate(${targetLeft - startLeft}px,${targetTop - startTop}px) scaleX(${targetWidth / flyingWidth})`, opacity: 1 }
      ], { duration: 900, easing: 'cubic-bezier(.35,.05,.2,1)', fill: 'forwards' });
      animations.add(animation);
      try { await animation.finished; } catch (error) { return; }
      animations.delete(animation);
      if (cancelled) return;
      const segment = segmentRefs.current[step.targetIndex];
      if (segment) segment.style.width = `${step.targetAmount / 3}%`;
      flying.classList.add(`land-${step.targetClass}`);
      await wait(120);
      flying.remove();
    };

    const run = async () => {
      if (reducedMotion) { showFinal(); return; }
      while (!cancelled) {
        reset();
        await wait(700);
        for (const step of steps) {
          await transfer(step);
          await wait(350);
        }
        await wait(2200);
      }
    };
    run();
    return () => { cancelled = true; animations.forEach(animation => animation.cancel()); stage.querySelectorAll('.flying-consumption').forEach(node => node.remove()); };
  }, [priority]);

  const resultSegments = priority ? [
    ['main low', 'Ana 150'], ['sub low', 'Süzme 90'], ['sub high', 'Süzme 60']
  ] : [['main low', 'Ana 120'], ['sub low', 'Süzme 120'], ['main high', 'Ana 40'], ['sub high', 'Süzme 20']];

  return <section className={`tier-allocation-visual physical-transfer ${mode}`} aria-labelledby={`${mode}-visual-title`}>
    <div className="allocation-heading"><div><span className="allocation-kicker">{priority ? 'Öncelik sırasıyla aktarım' : 'Eşit hakla aktarım'}</span><h4 id={`${mode}-visual-title`}>{priority ? 'Önce ana sayaç, sonra süzme sayaç' : 'Önce ucuz tarife eşit kullanılır, sonra pahalı tarifeye geçilir'}</h4></div><span className="loop-indicator" aria-hidden="true">Nasıl hesaplıyoruz?</span></div>
    <div ref={stageRef} className="physical-stage">
      <div className="source-area"><div className="source-meter main"><div className="source-label"><strong>Ana sayaç</strong><span>{priority ? 150 : 160} kWh</span></div><div className="source-track"><div ref={mainFillRef} className="source-fill"><span>{priority ? 150 : 160} kWh</span></div></div></div><div className="source-meter sub"><div className="source-label"><strong>Süzme sayaç</strong><span>{priority ? 150 : 140} kWh</span></div><div className="source-track"><div ref={subFillRef} className="source-fill"><span>{priority ? 150 : 140} kWh</span></div></div></div></div>
      <div className="physical-transfer-space"><b>↓</b></div>
      <div className="result-area"><div className="result-heading"><strong>Hesaplama</strong><span>Toplam 300 kWh</span></div><div className="result-scale"><span>0</span><span className="result-threshold-label">240 kWh düşük kademe sınırı</span><span>300</span></div><div ref={resultRef} className="physical-result-track" role="img" aria-label={priority ? 'Ana sayaçtan 150, süzme sayaçtan 90 kilovatsaat düşük ve 60 kilovatsaat yüksek kademeye fiziksel bloklar hâlinde aktarılır.' : 'Ana ve süzme sayaçtan 120’şer kilovatsaat düşük, kalan 40 ve 20 kilovatsaat yüksek kademeye fiziksel bloklar hâlinde aktarılır.'}>{resultSegments.map(([className, label], index) => <div key={label} ref={element => { segmentRefs.current[index] = element; }} className={`physical-result-segment ${className}`}><span>{label}</span></div>)}<i className="result-threshold" /></div></div>
    </div>
    <Legend />
  </section>;
};

export default TierAllocationVisual;
